import { useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { useReveal } from "@/lib/useReveal";
import { toast } from "sonner";
import { Send, MapPin, Mail, Phone } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";

const CONTACT_ENDPOINT = "/api/contact.php";

const initialState = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  message: "",
};

export default function Contact() {
  const { t } = useLanguage();
  const ref = useReveal();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.message) return;
    setSubmitting(true);
    try {
      await axios.post(CONTACT_ENDPOINT, form);
      toast.success(t.contact.success);
      setForm(initialState);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-6 sm:px-12 lg:px-24 py-24 sm:py-32 bg-[#0D0E13] border-y border-white/5"
    >
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div ref={ref} className="reveal lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D97736] mb-4">
            {t.contact.overline}
          </p>
          <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
            {t.contact.title}
          </h2>
          <p className="text-[#A0AAB2] text-lg leading-relaxed mb-12">
            {t.contact.subtitle}
          </p>

          <ul className="space-y-6">
            <li className="flex items-start gap-4">
              <a
                href="https://maps.google.de/maps?q=Buchstauden+16%2C+78269+Volkertshausen"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in Google Maps"
                className="w-10 h-10 grid place-items-center border border-white/15 text-[#D97736] hover:bg-[#D97736] hover:text-[#0B0C10] hover:border-[#D97736] transition-colors shrink-0"
              >
                <MapPin className="w-4 h-4" />
              </a>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#A0AAB2] mb-1">
                  Workshop
                </div>
                <a
                  data-testid="contact-address-link"
                  href="https://maps.google.de/maps?q=Buchstauden+16%2C+78269+Volkertshausen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#D97736] transition-colors block"
                >
                  Buchstauden 16
                  <br />
                  78269 Volkertshausen
                  <br />
                  <span className="text-[#A0AAB2] text-sm">
                    bei Singen / Konstanz / Schaffhausen
                    <br />
                    Grenzübergang Thayngen · südliches Ende A81
                  </span>
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <a
                href="mailto:post@schneggundweg.de"
                aria-label="Send email"
                className="w-10 h-10 grid place-items-center border border-white/15 text-[#D97736] hover:bg-[#D97736] hover:text-[#0B0C10] hover:border-[#D97736] transition-colors shrink-0"
              >
                <Mail className="w-4 h-4" />
              </a>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#A0AAB2] mb-1">
                  Email
                </div>
                <a
                  href="mailto:post@schneggundweg.de"
                  className="text-white hover:text-[#D97736] transition-colors"
                >
                  post@schneggundweg.de
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <a
                href="tel:+4977315071668"
                aria-label="Call"
                className="w-10 h-10 grid place-items-center border border-white/15 text-[#D97736] hover:bg-[#D97736] hover:text-[#0B0C10] hover:border-[#D97736] transition-colors shrink-0"
              >
                <Phone className="w-4 h-4" />
              </a>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#A0AAB2] mb-1">
                  Phone
                </div>
                <a
                  href="tel:+4977315071668"
                  className="text-white hover:text-[#D97736] transition-colors"
                >
                  +49 7731 5071668
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <a
                href="https://wa.me/4917680413791"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="w-10 h-10 grid place-items-center border border-white/15 text-[#D97736] hover:bg-[#D97736] hover:text-[#0B0C10] hover:border-[#D97736] transition-colors shrink-0"
              >
                <WhatsAppIcon className="w-4 h-4" />
              </a>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#A0AAB2] mb-1">
                  WhatsApp
                </div>
                <a
                  href="https://wa.me/4917680413791"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#D97736] transition-colors"
                >
                  +49 176 80413791
                </a>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-[#14161A] border border-white/5 p-8 sm:p-10 space-y-6"
            noValidate
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field
                label={t.contact.fields.name}
                testId="contact-name"
                required
              >
                <input
                  data-testid="contact-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={setField("name")}
                  className="w-full bg-black/30 border border-[#2C2F36] focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] outline-none text-white px-4 py-3 text-base"
                />
              </Field>

              <Field
                label={t.contact.fields.email}
                testId="contact-email"
                required
              >
                <input
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={setField("email")}
                  className="w-full bg-black/30 border border-[#2C2F36] focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] outline-none text-white px-4 py-3 text-base"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={t.contact.fields.phone} testId="contact-phone">
                <input
                  data-testid="contact-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={setField("phone")}
                  className="w-full bg-black/30 border border-[#2C2F36] focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] outline-none text-white px-4 py-3 text-base"
                />
              </Field>

              <Field
                label={t.contact.fields.project}
                testId="contact-project"
                required
              >
                <select
                  data-testid="contact-project-select"
                  required
                  value={form.project_type}
                  onChange={setField("project_type")}
                  className="w-full bg-black/30 border border-[#2C2F36] focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] outline-none text-white px-4 py-3 text-base appearance-none"
                >
                  <option value="" disabled>
                    —
                  </option>
                  {t.contact.project_options.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#14161A]">
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field
              label={t.contact.fields.message}
              testId="contact-message"
              required
            >
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={setField("message")}
                className="w-full bg-black/30 border border-[#2C2F36] focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] outline-none text-white px-4 py-3 text-base resize-none"
              />
            </Field>

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={submitting}
              className="group w-full sm:w-auto bg-[#D97736] hover:bg-[#C2662B] disabled:opacity-60 disabled:cursor-not-allowed text-white px-10 py-5 font-bold tracking-wider uppercase text-sm inline-flex items-center justify-center gap-3 transition-colors"
            >
              {submitting ? t.contact.sending : t.contact.submit}
              <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, required, children }) {
  return (
    <label data-testid={`${testId}-field`} className="block">
      <span className="block text-xs font-bold uppercase tracking-widest text-[#A0AAB2] mb-2">
        {label}
        {required && <span className="text-[#D97736]"> *</span>}
      </span>
      {children}
    </label>
  );
}
